import { handleApiResponse } from '@src/api/apiClient/axiosClient.ts';
import {
  dxDiagnosisResultApiClient,
  DxDiagnosisResultScoreIndexResponse,
  DxDiagnosisResultShowResponse,
} from '@src/api/company/dxDiagnosis/dxDiagnosisResultApiClient.ts';
import { useCompanyAuth } from '@src/context/auth/useCompanyAuth';
import Button from '@src/ui/elements/button/Button.tsx';
import DropdownCheckbox from '@src/ui/elements/form/DropdownCheckbox.tsx';
import DropdownMenu from '@src/ui/elements/form/DropdownMenu.tsx';
import logger from '@src/utils/logger.ts';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import Advice from './components/DxDiagnosisResults/Advice';
import AnswerAndScoreTab from './components/DxDiagnosisResults/AnswerAndScoreTab';
import HexagonRadarChart from './components/DxDiagnosisResults/HexagonRadarChart';
import LayerGraph from './components/DxDiagnosisResults/LayerGraph';
import TransitionLineGraph from './components/DxDiagnosisResults/TransitionLineGraph';

export type HexagonDataItem = {
  subject: string;
  value: number;
};

export type RecommendedSupportMenu = {
  title: string;
  href: string;
};

interface Option {
  value: string;
  label: React.ReactNode;
}

const RadarChartSubjects = [
  'DXマインド',
  'ブランディング',
  'DX戦略立案',
  '既存事業変革',
  '新規事業開発',
  '事業継続',
];

export type LineChartDataItem = {
  labels: string[];
  datasets: {
    label: string;
    data: { x: string; y: number | null; id: number; name: string }[];
    borderColor: string;
    tension: number;
    spanGaps: boolean;
  }[];
};

const lineChartColors = [
  '#E74C3C',
  '#3498DB',
  '#F1C40F',
  '#2ECC71',
  '#E67E22',
  '#9B59B6',
  '#E84393',
  '#1ABC9C',
  '#8E6E53',
  '#BDC3C7',
  '#B10D00',
  '#00589B',
  '#B18400',
  '#008C31',
  '#A63E00',
  '#692386',
  '#A80353',
  '#007C5C',
  '#653E1D',
  '#7D8387',
];

const DEFAULT_SELECTED_GRAPH_PERIOD_OPTION = {
  value: '0',
  label: 'すべての期間',
};

function DxDiagnosisResultForm() {
  const navigate = useNavigate();
  const { profile } = useCompanyAuth();
  const { id } = useParams<{ id: string }>();
  const [dxDiagnosisResultShowResponse, setDxDiagnosisResultShowResponse] =
    useState<DxDiagnosisResultShowResponse | null>(null);
  const [dxDiagnosisResultScoreIndexResponse, setDxDiagnosisResultScoreIndexResponse] =
    useState<DxDiagnosisResultScoreIndexResponse | null>(null);
  const [hexagonData, setHexagonData] = useState<HexagonDataItem[]>([]);
  const [recommendedSupportMenu, setRecommendedSupportMenu] = useState<RecommendedSupportMenu[]>(
    [],
  );
  const [lineChartData, setLineChartData] = useState<LineChartDataItem>({
    labels: [],
    datasets: [],
  } as LineChartDataItem);
  const [transitionGraphUserOptions, setTransitionGraphUserOptions] = useState<Option[]>([]);
  const [lineChartColorMap, setLineChartColorMap] = useState<Map<number, string>>(new Map());
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>(['0']);
  const [selectedGraphPeriodOption, setSelectedGraphPeriodOption] = useState<{
    label: string;
    value: string;
  }>(DEFAULT_SELECTED_GRAPH_PERIOD_OPTION);
  const [displayPeriod, setDisplayPeriod] = useState<string>('');

  const initLineChartColorMap = (data: DxDiagnosisResultScoreIndexResponse) => {
    const newColorMap = new Map<number, string>();
    data.forEach((user, index) => {
      const color = lineChartColors[index % lineChartColors.length];
      newColorMap.set(user.company_user.id, color);
    });
    setLineChartColorMap(newColorMap);
  };

  const formatLineChartData = (
    data: DxDiagnosisResultScoreIndexResponse,
    formattedStartDate?: string,
    formattedEndDate?: string,
  ): LineChartDataItem => {
    const allDates = Array.from(
      new Set(data.flatMap((user) => user.scores.map((score) => score.diagnosis_on))),
    ).sort();
    if (formattedStartDate && !allDates.includes(formattedStartDate)) {
      allDates.unshift(formattedStartDate);
    }
    if (formattedEndDate && !allDates.includes(formattedEndDate)) {
      allDates.push(formattedEndDate);
    }

    const datasets = data.map((user) => {
      const isLoggedUser = user.company_user.id === profile?.user?.id;
      const chartColor = lineChartColorMap.get(user.company_user.id) || '#000';
      return {
        label: `${user.company_user.family_name} ${user.company_user.first_name}`,
        data: allDates.map((date) => {
          const score = user.scores.find((s) => s.diagnosis_on === date);
          return {
            x: date,
            y: score ? score.total_score : null,
            id: user.company_user.id,
            name: `${user.company_user.family_name}${user.company_user.first_name}`,
          };
        }),
        borderColor: chartColor,
        tension: 0.1,
        spanGaps: true,
        pointRadius: isLoggedUser ? 4 : 3.5,
        borderWidth: isLoggedUser ? 2.5 : 1,
        backgroundColor: chartColor,
      };
    });

    return { labels: allDates, datasets };
  };

  const formatLineChartUserData = (scoreData: DxDiagnosisResultScoreIndexResponse) => {
    const options: Option[] = [
      {
        value: 'all',
        label: 'すべての回答者',
      },
      ...scoreData.map((user) => {
        const color = lineChartColorMap.get(user.company_user.id) || '#000';
        return {
          value: String(user.company_user.id),
          label: (
            <div className='flex items-center gap-1'>
              <span
                className={`size-3 flex-[0_0_auto] rounded-full`}
                style={{ backgroundColor: color }}
              ></span>
              <span>{`${user.company_user.family_name} ${user.company_user.first_name}`}</span>
            </div>
          ),
        };
      }),
    ];

    return options;
  };

  useEffect(() => {
    const fetchData = async () => {
      const res = await dxDiagnosisResultApiClient.show(Number(id));
      handleApiResponse(
        res,
        async (data) => {
          setDxDiagnosisResultShowResponse(data);
          const formattedHexagonData = RadarChartSubjects.map((subject) => {
            const matchingStep = data.steps.find((step) => step.name === subject);
            return {
              subject,
              value: matchingStep?.score || 0,
            };
          });
          setHexagonData(formattedHexagonData);

          const supportMenu = data.advice.support_services.map((service) => ({
            title: service.title,
            href: `/support-services/${service.id}`,
          }));
          setRecommendedSupportMenu(supportMenu);

          if (data.company?.id) {
            const scoreRes = await dxDiagnosisResultApiClient.scoreIndex({
              company_id: Number(data.company.id),
            });
            handleApiResponse(
              scoreRes,
              (scoreData) => {
                initLineChartColorMap(scoreData);
                setDxDiagnosisResultScoreIndexResponse(scoreData);
              },
              (error) => {
                logger.error('Failed to load dx diagnosis score data.', error);
              },
            );
          }
        },
        (error) => {
          logger.error('Failed to load dx diagnosis result data.', error);
        },
      );
    };
    fetchData();
  }, [id]);

  useEffect(() => {
    if (dxDiagnosisResultScoreIndexResponse && lineChartColorMap) {
      setLineChartData(formatLineChartData(dxDiagnosisResultScoreIndexResponse));

      const lineChartUserData = formatLineChartUserData(dxDiagnosisResultScoreIndexResponse);
      setTransitionGraphUserOptions(lineChartUserData);
    }
  }, [dxDiagnosisResultScoreIndexResponse, lineChartColorMap]);

  const basicInfoSectionData = [
    { label: '企業名', value: `${dxDiagnosisResultShowResponse?.company.name}` },
    { label: '業種', value: `${dxDiagnosisResultShowResponse?.company.industry_text}` },
    { label: '診断完了日', value: `${dxDiagnosisResultShowResponse?.diagnosis_on}` },
    { label: '診断バージョン', value: `${dxDiagnosisResultShowResponse?.version}` },
  ];

  const formatPeriodText = (start: Date, end: Date): string => {
    const startYear = start.getFullYear();
    const startMonth = start.getMonth() + 1;
    const endYear = end.getFullYear();
    const endMonth = end.getMonth() + 1;

    return `${startYear}年${startMonth}月〜${endYear}年${endMonth}月`;
  };

  const filterAndUpdateLineChartData = (period: string, userIds: string[]) => {
    if (!dxDiagnosisResultScoreIndexResponse) {
      return;
    }

    const currentDate = new Date();
    let startDate: Date | null = new Date(currentDate);
    let periodText = '';
    switch (period) {
      case '0':
        startDate = null;
        periodText = '';
        break;
      case '1':
        startDate = new Date(startDate.setMonth(currentDate.getMonth() - 3));
        periodText = formatPeriodText(startDate, currentDate);
        break;
      case '2':
        startDate = new Date(startDate.setMonth(currentDate.getMonth() - 6));
        periodText = formatPeriodText(startDate, currentDate);
        break;
      case '3':
        startDate = new Date(startDate.setMonth(currentDate.getMonth() - 9));
        periodText = formatPeriodText(startDate, currentDate);
        break;
      case '4':
        startDate = new Date(startDate.setFullYear(currentDate.getFullYear() - 1));
        periodText = formatPeriodText(startDate, currentDate);
        break;
      case '5':
        startDate = new Date(startDate.setFullYear(currentDate.getFullYear() - 2));
        periodText = formatPeriodText(startDate, currentDate);
        break;
    }

    setDisplayPeriod(periodText);

    const formattedStartDate = startDate ? startDate.toISOString().split('T')[0] : undefined;
    const formattedEndDate = startDate ? currentDate.toISOString().split('T')[0] : undefined;

    const filteredData = dxDiagnosisResultScoreIndexResponse
      .filter((user) => userIds.includes('0') || userIds.includes(String(user.company_user.id)))
      .map((user) => ({
        ...user,
        scores: user.scores.filter((score) => {
          if (!formattedStartDate) {
            return true;
          }
          return score.diagnosis_on >= formattedStartDate;
        }),
      }));

    setLineChartData(formatLineChartData(filteredData, formattedStartDate, formattedEndDate));
  };

  const transitionGraphPeriodOptions = [
    { ...DEFAULT_SELECTED_GRAPH_PERIOD_OPTION },
    { value: '1', label: '過去3ヶ月' },
    { value: '2', label: '過去6ヶ月' },
    { value: '3', label: '過去9ヶ月' },
    { value: '4', label: '過去12ヶ月' },
    { value: '5', label: '過去24ヶ月' },
  ];
  const handleTransitionGraphPeriodSelect = (option: Option) => {
    logger.log('Selected period:', option);

    setSelectedGraphPeriodOption({ value: option.value, label: option.label?.toString() || '' });
    filterAndUpdateLineChartData(option.value, selectedUserIds);
  };

  const handleTransitionGraphUserSelect = (selectedOptions: Option[]) => {
    const selectedIds = selectedOptions.map((option) => option.value);
    setSelectedUserIds(selectedIds);
    filterAndUpdateLineChartData(selectedGraphPeriodOption.value, selectedIds);
  };

  return (
    <main className='bg-[#f9f9f9] px-16 py-12 lg:px-5 lg:pt-24'>
      {dxDiagnosisResultShowResponse ? (
        <div className='m-auto max-w-[1152px]'>
          <h1 className='text-center text-2xl font-semibold'>DX診断結果レポート</h1>
          <div className='mt-8 rounded-sm bg-white p-6'>
            <dl className='flex flex-wrap gap-x-10 text-sm lg:gap-x-5 [&>*:nth-last-child(-n+2)]:border-b [&>*:nth-last-child(-n+2)]:border-b-gray-200'>
              {basicInfoSectionData.map((item, index) => (
                <div
                  key={index}
                  className='flex w-[calc(50%-20px)] border-t border-solid border-t-gray-200 lg:w-[calc(50%-10px)] '
                >
                  <dt className='w-2/5 bg-gray-10 px-[8%] py-5 font-semibold lg:px-[4%]'>
                    {item.label}
                  </dt>
                  <dd className='w-3/5 px-[8%] py-5 lg:px-[4%]'>{item.value}</dd>
                </div>
              ))}
            </dl>
          </div>
          <div className='mt-8 rounded-sm bg-white px-6 py-7'>
            <h2 className='text-2xl font-semibold'>診断結果</h2>
            <dl className='mt-4 flex flex-wrap gap-10 lg:gap-5'>
              <div className='flex w-[calc(50%-20px)] items-center border-y border-solid border-y-gray-200 lg:w-[calc(50%-10px)]'>
                <dt className='flex h-[64px] w-2/5 items-center bg-gray-10 px-[8%] text-sm font-semibold lg:px-[4%]'>
                  総合スコア
                </dt>
                <dd className='flex h-[64px] w-3/5 items-center px-[8%] lg:px-[4%]'>
                  <div>
                    <span className='text-3xl font-semibold leading-none text-primary-500'>
                      {dxDiagnosisResultShowResponse.total_score}
                    </span>
                    点／120点
                  </div>
                </dd>
              </div>
              <div className='flex w-[calc(50%-20px)] items-center border-y border-solid border-y-gray-200 lg:w-[calc(50%-10px)]'>
                <dt className='flex h-[64px] w-2/5 items-center bg-gray-10 px-[8%] text-sm font-semibold lg:px-[4%]'>
                  DXステージ
                </dt>
                <dd className='flex h-[64px] w-3/5 items-center px-[8%] text-xl font-semibold text-primary-500 lg:px-[4%]'>
                  {dxDiagnosisResultShowResponse.stage.name}
                </dd>
              </div>
            </dl>
            <div className='mt-6 flex gap-10 lg:gap-5'>
              <div className='w-[calc(50%-20px)] border border-solid border-gray-200 lg:w-[calc(50%-10px)]'>
                <HexagonRadarChart data={hexagonData} />
              </div>
              <div className='w-[calc(50%-20px)] border border-solid border-gray-200 lg:w-[calc(50%-10px)]'>
                <LayerGraph currentLayer={dxDiagnosisResultShowResponse.stage.code} />
              </div>
            </div>
          </div>
          <div className='mt-8 rounded-sm bg-white px-6 py-7'>
            <h2 className='text-2xl font-semibold'>選択した回答とスコア</h2>
            <div className='mt-3'>
              <AnswerAndScoreTab data={dxDiagnosisResultShowResponse.steps || []} />
            </div>
          </div>
          <div className='mt-8 rounded-sm bg-white px-6 py-7'>
            <h2 className='text-2xl font-semibold'>DXステップ別アドバイス</h2>
            <p className='mt-6 text-sm leading-relaxed'>
              {dxDiagnosisResultShowResponse.advice.content.explanation}
            </p>
            <div className='mt-10'>
              <Advice
                adviceContent={dxDiagnosisResultShowResponse.advice.content}
                recommendedSupportMenu={recommendedSupportMenu}
              />
            </div>
          </div>
          <div className='mt-8 bg-white px-6 py-7'>
            <div className='flex justify-between'>
              <h2 className='text-2xl font-semibold'>
                DX成熟度の推移{' '}
                {displayPeriod !== '' && <span className='ml-2 text-lg'>({displayPeriod})</span>}
              </h2>
              <div className='flex items-center gap-8'>
                <div className='flex items-center gap-3'>
                  <p className='text-[10px]'>期間選択</p>
                  <div className='w-[160px]'>
                    <DropdownMenu
                      options={transitionGraphPeriodOptions}
                      onSelect={handleTransitionGraphPeriodSelect}
                      defaultValue={selectedGraphPeriodOption}
                      placeholder='項目を選択'
                    />
                  </div>
                </div>
                <div className='flex items-center gap-3'>
                  <p className='text-[10px]'>表示データ</p>
                  <div className='w-[160px]'>
                    <DropdownCheckbox
                      options={transitionGraphUserOptions}
                      onSelect={handleTransitionGraphUserSelect}
                      placeholder='項目を選択'
                      allSelectedText='すべての回答者'
                      selectedCountText={(count) => `${count}名の回答者`}
                      menuWidth={200}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className='mt-6'>
              <TransitionLineGraph data={lineChartData} />
            </div>
          </div>
          <div className='mt-12 flex justify-center'>
            <div className='w-[180px]'>
              <Button
                fullRounded
                fullWidth
                onClick={() => navigate('/dx-diagnosis')}
              >
                診断結果一覧へ
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <></>
      )}
    </main>
  );
}

export default DxDiagnosisResultForm;
